import React from 'react';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

/**
 * Props untuk komponen ErrorState.
 * @param error - Objek error yang akan dianalisis. Bisa dari Axios, React Query, atau error lainnya.
 * @param onRetry - Callback function yang dipanggil saat tombol "Try Again" diklik.
 */
interface ErrorStateProps {
    error: unknown; // Menggunakan 'unknown' adalah praktik terbaik untuk error di TypeScript
    onRetry: () => void;
}

/**
 * Komponen untuk menampilkan state error yang informatif dan dapat ditindaklanjuti.
 * Ia menampilkan pesan yang berbeda berdasarkan tipe error (misalnya, network vs. server error)
 * dan menyediakan tombol untuk mencoba kembali.
 */
const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => {

    /**
     * Menganalisis objek error dan mengembalikan pesan yang sesuai untuk ditampilkan kepada pengguna.
     * @returns Objek berisi judul dan deskripsi pesan error.
     */
    const getErrorMessage = (): { title: string; description: string } => {
        // Default message untuk error yang tidak terduga
        let title = 'An Unexpected Error Occurred';
        let description = 'Something went wrong. Please try again or contact support if the problem persists.';

        // Cek jika ini adalah error dari Axios
        if (typeof error === 'object' && error !== null && 'isAxiosError' in error && error.isAxiosError) {
            const axiosError = error as { response?: { status: number; data: { message?: string } } };

            if (axiosError.response) {
                // Error dengan respons dari server (e.g., 404, 500)
                const status = axiosError.response.status;
                const serverMessage = axiosError.response.data?.message;

                if (status >= 500) {
                    title = 'Server Error';
                    description = serverMessage || 'We are experiencing issues on our end. Our team has been notified. Please try again later.';
                } else if (status === 404) {
                    title = 'Resource Not Found';
                    description = 'The resource you are looking for could not be found.';
                } else if (status >= 400) {
                    title = 'Request Error';
                    description = serverMessage || `There was a problem with the request (Status ${status}).`;
                }
            } else {
                // Error tanpa respons dari server (e.g., network error)
                title = 'Network Connection Error';
                description = 'Could not connect to the server. Please check your internet connection and try again.';
            }
        } else if (error instanceof Error) {
            // Error JavaScript standar
            description = error.message;
        }

        return { title, description };
    };

    const { title, description } = getErrorMessage();

    return (
        <div
            className="d-flex flex-column justify-content-center align-items-center text-center p-4"
            style={{ minHeight: '400px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
        >
            <IconAlertTriangle size={64} className="text-danger mb-3" />
            <h3 className="mb-2">{title}</h3>
            <p className="text-muted mb-4" style={{ maxWidth: '450px' }}>
                {description}
            </p>
            <button className="btn btn-primary d-flex align-items-center" onClick={onRetry}>
                <IconRefresh size={18} className="me-2" />
                Try Again
            </button>
        </div>
    );
};

export default ErrorState;